import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { nav } from '../mock';
import Logo from './Logo';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const bandRef = useRef(null);
  const loc = useLocation();

  useEffect(() => { setOpen(false); setExpanded(null); setOpenMenu(null); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const onDown = (e) => { if (bandRef.current && !bandRef.current.contains(e.target)) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  return (
    <header className={`sticky top-0 z-40 w-full transition-all ${scrolled ? 'shadow-[0_10px_30px_-24px_rgba(74,18,89,.5)]' : ''}`}>
      {/* Row 1 — identity */}
      <div className={`w-full transition-colors ${scrolled ? 'bg-[var(--mm-cream)]/95 backdrop-blur' : 'bg-[var(--mm-cream)]'}`}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[74px] flex items-center justify-between">
          <Logo />
          <div className="hidden lg:block">
            <Link to="/contact" className="btn-primary text-xs" data-testid="header-cta">Talk to us</Link>
          </div>
          <button className="lg:hidden w-10 h-10 flex items-center justify-center text-[var(--mm-purple)]" onClick={() => setOpen(v => !v)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Row 2 — horizontal navigation band, click to open */}
      <div ref={bandRef} className="hidden lg:block w-full bg-[var(--mm-purple)] border-y border-[var(--mm-purple-dark)]" data-testid="nav-band">
        <nav className="max-w-7xl mx-auto px-5 md:px-8 flex items-stretch">
          {nav.map((n) => {
            const active = loc.pathname === n.href || (n.children && n.children.some(c => c.href === loc.pathname));
            const isOpen = openMenu === n.label;
            const key = n.label.toLowerCase().replace(/[^a-z]+/g, '-');
            return (
              <div key={n.label} className="relative">
                {n.children ? (
                  <button
                    onClick={() => setOpenMenu(v => (v === n.label ? null : n.label))}
                    aria-expanded={isOpen}
                    data-testid={`nav-${key}`}
                    className={`h-12 px-4 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.1em] transition-colors border-b-2 ${isOpen || active ? 'text-white border-[var(--mm-magenta)] bg-white/5' : 'text-white/70 border-transparent hover:text-white hover:bg-white/5'}`}>
                    {n.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={n.href}
                    data-testid={`nav-${key}`}
                    className={`h-12 px-4 inline-flex items-center text-[12px] font-semibold uppercase tracking-[0.1em] transition-colors border-b-2 ${active ? 'text-white border-[var(--mm-magenta)] bg-white/5' : 'text-white/70 border-transparent hover:text-white hover:bg-white/5'}`}>
                    {n.label}
                  </Link>
                )}

                {n.children && isOpen && (
                  <div className="absolute left-0 top-full pt-2 z-50" data-testid={`nav-dropdown-${key}`}>
                    <div className="w-[380px] bg-white rounded-xl shadow-[0_24px_60px_-24px_rgba(74,18,89,.45)] border border-[var(--mm-line)] py-2 overflow-hidden">
                      {n.children.map((c) => (
                        <Link
                          key={c.href}
                          to={c.href}
                          onClick={() => setOpenMenu(null)}
                          data-testid={`nav-item-${c.href.replace(/\//g, '-').replace(/^-/, '')}`}
                          className={`block px-4 py-3 transition-colors border-l-2 ${loc.pathname === c.href ? 'bg-[var(--mm-cream)] border-[var(--mm-magenta)]' : 'border-transparent hover:bg-[var(--mm-cream)] hover:border-[var(--mm-purple)]'}`}>
                          <span className={`block text-sm font-semibold ${loc.pathname === c.href ? 'text-[var(--mm-purple)]' : 'text-[var(--mm-ink)]'}`}>{c.label}</span>
                          {c.desc && <span className="block mt-1 text-xs leading-relaxed text-[var(--mm-ink)]/60">{c.desc}</span>}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--mm-line)] bg-[var(--mm-cream)] max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-2">
            {nav.map((n) => (
              <div key={n.label} className="border-b border-[var(--mm-line)] last:border-0">
                {n.children ? (
                  <>
                    <button onClick={() => setExpanded(e => e === n.label ? null : n.label)} className="w-full flex items-center justify-between py-3 text-sm font-semibold text-[var(--mm-ink)]" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                      {n.label}<ChevronDown className={`w-4 h-4 transition-transform ${expanded === n.label ? 'rotate-180' : ''}`} />
                    </button>
                    {expanded === n.label && (
                      <div className="pb-3 pl-3 flex flex-col gap-1">
                        {n.children.map(c => (
                          <Link key={c.href} to={c.href} className="py-2">
                            <span className="block text-sm font-semibold text-[var(--mm-ink)]/85">{c.label}</span>
                            {c.desc && <span className="block text-xs text-[var(--mm-ink)]/55 mt-0.5 leading-relaxed">{c.desc}</span>}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={n.href} className="block py-3 text-sm font-semibold text-[var(--mm-ink)]">{n.label}</Link>
                )}
              </div>
            ))}
            <div className="py-4"><Link to="/contact" className="btn-primary w-full justify-center">Talk to us</Link></div>
          </div>
        </div>
      )}
    </header>
  );
}
